import { type FocusEvent, useCallback, useEffect } from 'react';
import type { Key } from 'react-aria-components';
import { CheckoutSection } from '@/common.styled';
import { AddressFields } from '@/components/AddressFields';
import { BillingAddressDisclosure } from '@/components/BillingAddressDisclosure';
import { Heading } from '@/components/Heading';
import {
  FieldName,
  ValidationMessage,
  ValidationSectionKey,
} from '@/constants';
import { usePageData } from '@/contexts/pageContext';
import { useShipping } from '@/contexts/shippingContext';
import type { RequiredFormField } from '@/contexts/types';
import { useValidation } from '@/contexts/validationContext';
import { useGetCounties } from '@/hooks/useGetCounties';
import type { AddressFieldKey } from '@/types';
import { doNothingOnSubmit } from '@/utils/doNothingOnSubmit';
import { getCountyLabel } from '@/utils/getCountyLabel';
import { getPostalCodeLabel } from '@/utils/getPostalCodeLabel';
import { getRequiredAddressFields } from '@/utils/getRequiredAddressFields';
import { validatePhone } from '@/utils/validatePhone';

export const BillingAddress = () => {
  const { countries } = usePageData();
  const {
    isClickAndCollect,
    isDelivery,
    orderPayload: { billingAddress },
    setBillingAddress,
  } = useShipping();

  const { countryCode } = billingAddress;
  const counties = useGetCounties(countryCode);
  const countyLabel = getCountyLabel(countryCode);
  const postalCodeLabel = getPostalCodeLabel(countryCode);

  const {
    addressFieldErrors,
    clearAddressFieldError,
    clearValidationError,
    errorMessages,
    setAddressFieldError,
  } = useValidation();

  const addressError = errorMessages[ValidationSectionKey.BillingAddress];

  const validationErrors = addressFieldErrors.billing;

  useEffect(() => {
    if (Object.values(validationErrors).filter(Boolean).length === 0) {
      clearValidationError(ValidationSectionKey.BillingAddress);
    }
  }, [clearValidationError, validationErrors]);

  const updateValidation = useCallback(
    (fieldName: RequiredFormField, value: string) => {
      // Phone validation: check format and length
      if (fieldName === FieldName.Phone) {
        if (!validatePhone(value)) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.InvalidPhone,
          );
        } else if (value.length > 19) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.PhoneTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      } else if (fieldName === FieldName.FirstName) {
        if (value.length > 20) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.FirstNameTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      } else if (fieldName === FieldName.LastName) {
        if (value.length > 20) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.LastNameTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      } else if (fieldName === FieldName.AddressLine1) {
        if (value.length > 50) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.AddressLineTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      } else if (fieldName === FieldName.City) {
        if (value.length > 50) {
          setAddressFieldError(
            'billing',
            fieldName,
            ValidationMessage.CityTooLong,
          );
        } else {
          clearAddressFieldError('billing', fieldName);
        }
      }
      // For other fields (postalCode, countryCode, etc.), don't clear errors here
      // They have their own validation logic
    },
    [clearAddressFieldError, setAddressFieldError],
  );

  const handlePhoneBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (value) {
        updateValidation(FieldName.Phone, value);
      } else {
        setAddressFieldError(
          'billing',
          FieldName.Phone,
          ValidationMessage.MissingField,
        );
      }
    },
    [setAddressFieldError, updateValidation],
  );

  const handleChange = useCallback(
    (fieldName: AddressFieldKey) => (value: Key | null) => {
      const rawValue = typeof value === 'string' ? value : '';
      const normalisedValue = rawValue.trim();

      // Validate raw length (including spaces) before trimming for length-constrained fields
      const isFirstName = fieldName === FieldName.FirstName;
      const isLastName = fieldName === FieldName.LastName;
      const isPhone = fieldName === FieldName.Phone;
      const isAddressLine1 = fieldName === FieldName.AddressLine1;
      const isCity = fieldName === FieldName.City;

      if (isFirstName || isLastName || isPhone || isAddressLine1 || isCity) {
        let errorMessage: string | null = null;
        if (isFirstName && rawValue.length > 20) {
          errorMessage = ValidationMessage.FirstNameTooLong;
        } else if (isLastName && rawValue.length > 20) {
          errorMessage = ValidationMessage.LastNameTooLong;
        } else if (isPhone && rawValue.length > 19) {
          errorMessage = ValidationMessage.PhoneTooLong;
        } else if (isAddressLine1 && rawValue.length > 50) {
          errorMessage = ValidationMessage.AddressLineTooLong;
        } else if (isCity && rawValue.length > 50) {
          errorMessage = ValidationMessage.CityTooLong;
        }

        if (errorMessage) {
          // Type assertion is safe here because we've checked the field is one of the required fields
          if (isFirstName) {
            setAddressFieldError('billing', FieldName.FirstName, errorMessage);
          } else if (isLastName) {
            setAddressFieldError('billing', FieldName.LastName, errorMessage);
          } else if (isPhone) {
            setAddressFieldError('billing', FieldName.Phone, errorMessage);
          } else if (isAddressLine1) {
            setAddressFieldError('billing', FieldName.AddressLine1, errorMessage);
          } else if (isCity) {
            setAddressFieldError('billing', FieldName.City, errorMessage);
          }
        }
      }

      const requiredFields = getRequiredAddressFields(countryCode);

      const isRequired = requiredFields.some(key => key === fieldName);

      // Validation only for required fields
      if (isRequired) {
        if (normalisedValue) {
          // For length-constrained fields, use raw value for length validation
          // For phone, always use raw value to check total length including formatting
          const valueToValidate =
            isPhone || isFirstName || isLastName || isAddressLine1 || isCity
              ? rawValue
              : normalisedValue;
          updateValidation(fieldName as RequiredFormField, valueToValidate);
        } else {
          setAddressFieldError(
            'billing',
            fieldName as RequiredFormField,
            ValidationMessage.MissingField,
          );
        }
      }
      // Note: Error clearing for length-constrained fields is handled by updateValidation
      // No need to clear errors here to avoid conflicts

      // Validate optional AddressLine2 length
      if (
        fieldName === FieldName.AddressLine2 &&
        normalisedValue &&
        normalisedValue.length > 50
      ) {
        // AddressLine2 is optional, so we can't use setAddressFieldError
        // Validation will be handled in validateAddress for submission
      }

      // Address update for ALL fields (required and optional)
      // Store trimmed value for text fields to prevent storing leading/trailing spaces
      if (fieldName === FieldName.Country) {
        const selectedCountry = countries?.find(
          country => value === country.countryCode,
        );
        if (selectedCountry) {
          // When country changes, also clear any previously selected county
          // so the user must select a valid county for the new country
          setBillingAddress({
            [FieldName.Country]: selectedCountry.countryCode,
            [FieldName.County]: '',
          });
        }
      } else if (fieldName === FieldName.County) {
        const selectedCounty = counties?.find(
          county => value === county.countyCode,
        );
        if (selectedCounty) {
          setBillingAddress({
            [FieldName.County]: selectedCounty.countyCode,
          });
        }
      } else {
        // Store trimmed value for text fields to avoid storing leading/trailing spaces
        const valueToStore =
          typeof value === 'string' ? normalisedValue : value;
        setBillingAddress({ [fieldName]: valueToStore });
      }
    },
    [
      counties,
      countries,
      countryCode,
      clearAddressFieldError,
      setAddressFieldError,
      setBillingAddress,
      updateValidation,
    ],
  );

  return (
    <>
      {isDelivery ? <BillingAddressDisclosure /> : null}
      {isClickAndCollect ? (
        <CheckoutSection
          data-validation-key={ValidationSectionKey.BillingAddress}
        >
          <Heading level={2}>Billing address</Heading>
          <div className={addressError ? 'has-validation-error' : undefined}>
            {addressError ? (
              <p aria-live="assertive" className="validation-error">
                {addressError}
              </p>
            ) : null}
            <AddressFields
              address={billingAddress}
              countyLabel={countyLabel}
              handleChange={handleChange}
              handlePhoneBlur={handlePhoneBlur}
              handleSubmit={doNothingOnSubmit}
              isBillingAddress={true}
              postalCodeLabel={postalCodeLabel}
              validationErrors={validationErrors}
            />
          </div>
        </CheckoutSection>
      ) : null}
    </>
  );
};
